import {html, LitElement, nothing} from "lit";
import {customElement, query, state} from "lit/decorators.js";
import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/Checkbox.js";
import "../../common/controls-inputs/LabeledTextInput.js";
import {renderPowerNotesLogo} from "../../common/powerNotesLogo.js";
import Data from "../../../main/data/index.js";
import {validate, emailValidators, passwordValidators} from "../../../main/utils/form.js";
import {Modal, MODALS} from "../../common/modals.js";
import {modalStyles} from "../../common/styles/Modals.js";
import Integration from "../../../integrations/integration/index.js";

@customElement("pn-int-signup-modal")
export class IntegrationSignupModal extends LitElement {
	@state() name = "";
	@state() email = "";
	@state() password = "";
	@state() terms = false;
	@state() success = false;
	@state() error: string | boolean = false;
	@state() fieldErrors = {
		name:     false,
		email:    false,
		password: false,
		terms:    false
	};

	@query(".modal-content") modalContent: HTMLElement;

	modal: Modal | null = null;

	static styles = [modalStyles];

	firstUpdated() {
		this.modal = new Modal({
			id:               "integration-signup",
			modalContent:     this.modalContent,
			parent:           this.shadowRoot,
			isSingleUseModal: true
		});
		this.modal.show();
	}

	validateForm() {
		this.fieldErrors = {
			email:    validate(this.email, emailValidators),
			name:     this.name.trim().length === 0,
			password: validate(this.password, passwordValidators),
			terms:    !this.terms
		};

		return Object.values(this.fieldErrors).find(v => !!v);
	}

	onClickSignup = async () => {
		const hasErrors = this.validateForm();
		if (hasErrors)
			return;

		try {
			await Data.app.integrationSignup(this.name, this.email, this.password, this.terms);
			Integration.gtag("event", "sign_up");
			this.success = true;
			this.error = false;
		} catch (e) {
			console.error(e);
			this.success = false;
			this.error = e.message;
		}
	};

	keydownOnForm = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();

			this.onClickSignup();
		}
	};

	openLoginModal = () => {
		this.modal?.openNewModal(MODALS.INT_LOGIN);
	};

	renderLogo() {
		return html`<div id="pn-modal-logo">${renderPowerNotesLogo()}<div>`;
	}

	renderForm() {
		let message;
		switch (Data.app.me?.integration) {
			case "proquest":
				message = "Thanks for using ProQuest! As a ProQuest user, you are eligible for 5 free projects with PowerNotes!";
				break;

			default:
				message = "Create an account to continue.";
		}

		return html`
			<div class="heading"><h2>${this.renderLogo()} Create Account</h2></div>

			<div class="body">
				<p>${message}</p>
				<form>
					<pn-labeled-text-input
						autocomplete="name"
						.error=${this.fieldErrors.name}
						id="pn-signup-name"
						inputType="text"
						label="Name"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.name = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.name}
					></pn-labeled-text-input>
					<pn-labeled-text-input
						autocomplete="email"
						.error=${this.fieldErrors.email}
						id="pn-signup-email"
						inputType="email"
						label="Email"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.email = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.email}
					></pn-labeled-text-input>
					<pn-labeled-text-input
						autocomplete="off"
						.error=${this.fieldErrors.password}
						id="pn-signup-pw"
						inputType="password"
						label="Password"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.password = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.password}
					></pn-labeled-text-input>

					<pn-checkbox
						.error=${this.fieldErrors.terms}
						.onChange=${(e: {target: {checked: boolean}}) => {
							this.terms = e.target.checked;
						}}
						.checked=${this.terms}
					>I agree to the PowerNotes <a
						.href=${Data.app.config?.urls.promo + "legal/terms"}
						target="_blank"
					>Terms of Use</a>*</pn-checkbox>
				</form>
			</div>

			<div class="buttons">
				<pn-button
					.onClick=${this.onClickSignup}
					renderType="auth"
				>Create Account</pn-button>
				
				${this.error ? html`<div id="modal-error">
					<p class="modal-error-msg msg-error-with-bg">${this.error === true ? "Unknown error" : this.error}</p>
				</div>` : nothing}
			</div>

			<div class="footer">
				Already have an account? <pn-button
					.onClick=${this.openLoginModal}
					renderType="link-blue"
				>Log in</pn-button>
			</div>
		`;
	}

	renderSuccess() {
		let message;
		switch (Data.app.me?.integration) {
			case "proquest":
				message = "Please check your inbox for an email validation link, "
					+ "then continue to save text, notes, citations, bookmarks, and more "
					+ "in the Congressional PowerNotes panel.";
				break;

			default:
				message = "Please check your inbox for an email validation link, "
					+ "then continue to save text, notes, citations, bookmarks, and more "
					+ "in the PowerNotes panel.";
		}

		return html`
			<div class="heading"><h2>${this.renderLogo()} Account Created</h2></div>
			
			<div class="body">
				<p><strong>Welcome!</strong></p>

				<p>${message}</p>

				<p><a
					.href=${Data.app.config?.urls.promo}
					target="_blank"
				>Learn more about PowerNotes</a></p>
			</div>
		`;
	}

	render() {
		return html`
			<div class="modal-content">
				<div id="pn-modal-content">
					${this.success ? this.renderSuccess() : this.renderForm()}
				</div>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-int-signup-modal": IntegrationSignupModal;
	}
}
